import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    phone: '',
    email: '',
    inquiryType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'お問い合わせ',
    description: 'M&Aに関するご相談やご質問など、お気軽にお問い合わせください。専門のアドバイザーが丁寧にご対応いたします。',
    url: `${siteUrl}/contact`
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // バリデーション
    if (!formData.companyName || !formData.name || !formData.phone || !formData.email || !formData.inquiryType || !formData.message) {
      alert('すべての項目を入力してください');
      return;
    }

    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('companyName', formData.companyName);
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('inquiryType', formData.inquiryType);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4n7cp9btbscp07qba80', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          name: '',
          phone: '',
          email: '',
          inquiryType: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO
        title="お問い合わせ | 株式会社G11"
        description="M&Aに関するご相談やご質問など、お気軽にお問い合わせください。専門のアドバイザーが丁寧にご対応いたします。会社売却、会社買収、事業承継のご相談を承ります。"
        keywords="お問い合わせ,M&A相談,会社売却相談,会社買収相談,事業承継相談"
        jsonLd={jsonLd}
      />
      <Navbar />
      
      <main className="flex-1 pt-20">
        {/* ヒーローセクション */}
        <section className="relative py-20 bg-gradient-to-br from-teal-50 to-blue-50">
          <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                お問い合わせ
              </h1>
              <p className="text-xl text-gray-600">
                M&Aに関するご相談やご質問など、お気軽にお問い合わせください。<br />
                専門のアドバイザーが丁寧にご対応いたします。
              </p>
            </div>
          </div>
        </section>

        {/* フォームセクション */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-12">
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  {/* 会社名 */}
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-semibold text-gray-700 mb-2">
                      会社名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="株式会社〇〇"
                    />
                  </div>

                  {/* お名前 */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="山田 太郎"
                    />
                  </div>

                  {/* 電話番号 */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      電話番号 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="03-1234-5678"
                    />
                  </div>

                  {/* メールアドレス */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="example@company.co.jp"
                    />
                  </div>

                  {/* お問い合わせ種別 */}
                  <div>
                    <label htmlFor="inquiryType" className="block text-sm font-semibold text-gray-700 mb-2">
                      お問い合わせ種別 <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="inquiryType"
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer bg-white"
                    >
                      <option value="">選択してください</option>
                      <option value="購入希望">購入希望</option>
                      <option value="売却希望">売却希望</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>

                  {/* お問い合わせ内容 */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      お問い合わせ内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                      placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1 text-right">
                      {formData.message.length} / 500文字
                    </p>
                  </div>

                  {/* 送信ボタン */}
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isSubmitting ? '送信中...' : '送信する'}
                    </button>
                  </div>

                  {/* ステータスメッセージ */}
                  {submitStatus === 'success' && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 text-sm text-center">
                        お問い合わせを受け付けました。担当者より折り返しご連絡いたします。
                      </p>
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800 text-sm text-center">
                        送信に失敗しました。もう一度お試しください。
                      </p>
                    </div>
                  )}
                </form>
              </div>

              {/* 連絡先情報 */}
              {/* 削除されました */}

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
